import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

export interface AppTab {
  id: string
  title: string
  path: string
  closable: boolean
  pinned: boolean
  hasUpdate: boolean
  state: Record<string, unknown>
}

const STORAGE_KEY = 'vhq_tabs'
const PINNED_STORAGE_KEY = 'vhq_pinned_tabs'
const MAX_TABS = 20

let nextId = Date.now()
function generateId(): string {
  return `tab-${nextId++}`
}

/** Append _t=<id> query param to make duplicate tab paths unique for KeepAlive */
function appendTabParam(path: string, tabId: string): string {
  const separator = path.includes('?') ? '&' : '?'
  return `${path}${separator}_t=${tabId}`
}

function loadTabs(): { tabs: AppTab[]; activeTabId: string | null } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed.tabs?.length) {
        // Backfill state field for tabs saved before this feature
        parsed.tabs = parsed.tabs.map((t: AppTab) => ({ ...t, state: t.state || {} }))
        return parsed
      }
    }
  } catch { /* ignore */ }

  // No saved tabs — restore pinned favorites if any
  try {
    const pinned = localStorage.getItem(PINNED_STORAGE_KEY)
    if (pinned) {
      const pinnedTabs: AppTab[] = JSON.parse(pinned)
      if (pinnedTabs.length) {
        // Re-generate IDs for restored pinned tabs
        const restored = pinnedTabs.map(t => ({
          ...t,
          id: generateId(),
          hasUpdate: false,
          state: t.state || {},
        }))
        return { tabs: restored, activeTabId: restored[0].id }
      }
    }
  } catch { /* ignore */ }

  return { tabs: [], activeTabId: null }
}

export const useTabStore = defineStore('tabs', () => {
  const saved = loadTabs()
  const tabs = ref<AppTab[]>(saved.tabs)
  const activeTabId = ref<string | null>(saved.activeTabId)

  const activeTab = computed(() => tabs.value.find(t => t.id === activeTabId.value))
  const closableTabs = computed(() => tabs.value.filter(t => t.closable))
  const tabCount = computed(() => tabs.value.length)

  function persist() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      tabs: tabs.value,
      activeTabId: activeTabId.value,
    }))
    // Always keep pinned tabs saved separately so they survive tab clears
    persistPinned()
  }

  function persistPinned() {
    const pinned = tabs.value.filter(t => t.pinned)
    if (pinned.length) {
      localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(pinned))
    } else {
      localStorage.removeItem(PINNED_STORAGE_KEY)
    }
  }

  /**
   * Add a tab. By default, if a tab with the same path exists, it activates that tab.
   * Pass `duplicate: true` to force open a second tab to the same path.
   */
  function addTab(path: string, title: string, options?: { duplicate?: boolean }): string {
    // Default: activate existing tab with same path (unless duplicate requested)
    if (!options?.duplicate) {
      const existing = tabs.value.find(t => t.path === path)
      if (existing) {
        activeTabId.value = existing.id
        persist()
        return existing.id
      }
    }

    // Enforce max tabs — remove oldest closable if at limit
    if (tabs.value.length >= MAX_TABS) {
      const oldest = tabs.value.find(t => t.closable && !t.pinned && t.id !== activeTabId.value)
      if (oldest) {
        tabs.value = tabs.value.filter(t => t.id !== oldest.id)
      }
    }

    const id = generateId()
    // For duplicate tabs, append a unique query param so KeepAlive
    // creates a separate component instance per tab
    const tabPath = options?.duplicate ? appendTabParam(path, id) : path
    tabs.value.push({
      id,
      title,
      path: tabPath,
      closable: true,
      pinned: false,
      hasUpdate: false,
      state: {},
    })
    activeTabId.value = id
    persist()
    return id
  }

  function closeTab(id: string): string | null {
    const tab = tabs.value.find(t => t.id === id)
    if (!tab || !tab.closable) return null

    const idx = tabs.value.indexOf(tab)
    tabs.value = tabs.value.filter(t => t.id !== id)

    // If closing the active tab, switch to adjacent
    if (activeTabId.value === id) {
      if (tabs.value.length === 0) {
        activeTabId.value = null
        persist()
        return '/dashboard'
      }
      const nextIdx = Math.min(idx, tabs.value.length - 1)
      activeTabId.value = tabs.value[nextIdx].id
      persist()
      return tabs.value[nextIdx].path
    }

    persist()
    return null
  }

  function closeOtherTabs(keepId: string) {
    tabs.value = tabs.value.filter(t => t.id === keepId || t.pinned || !t.closable)
    if (!tabs.value.find(t => t.id === activeTabId.value)) {
      activeTabId.value = keepId
    }
    persist()
  }

  function closeAllTabs(): string {
    tabs.value = tabs.value.filter(t => t.pinned || !t.closable)
    if (tabs.value.length > 0) {
      activeTabId.value = tabs.value[tabs.value.length - 1].id
      persist()
      return tabs.value[tabs.value.length - 1].path
    }
    activeTabId.value = null
    persist()
    return '/dashboard'
  }

  function setActiveTab(id: string) {
    activeTabId.value = id
    // Clear update indicator when tab is activated
    const tab = tabs.value.find(t => t.id === id)
    if (tab && tab.hasUpdate) {
      tab.hasUpdate = false
    }
    persist()
  }

  /**
   * Mark tabs as updated when their data changes while inactive.
   * Matches by path prefix — e.g. '/tickets' marks both the list tab
   * and any open ticket detail tabs.
   */
  function markTabUpdated(pathPrefix: string) {
    for (const tab of tabs.value) {
      if (tab.id !== activeTabId.value && tab.path.startsWith(pathPrefix)) {
        tab.hasUpdate = true
      }
    }
    persist()
  }

  /** Clear update flag on a specific tab */
  function clearTabUpdate(id: string) {
    const tab = tabs.value.find(t => t.id === id)
    if (tab) {
      tab.hasUpdate = false
      persist()
    }
  }

  function pinTab(id: string) {
    const tab = tabs.value.find(t => t.id === id)
    if (tab) {
      tab.pinned = true
      tab.closable = false
      // Move pinned tabs to the left
      const pinned = tabs.value.filter(t => t.pinned)
      const unpinned = tabs.value.filter(t => !t.pinned)
      tabs.value = [...pinned, ...unpinned]
      persist()
    }
  }

  function unpinTab(id: string) {
    const tab = tabs.value.find(t => t.id === id)
    if (tab) {
      tab.pinned = false
      tab.closable = true
      persist()
    }
  }

  function moveTab(fromIndex: number, toIndex: number) {
    if (fromIndex === toIndex) return
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= tabs.value.length || toIndex >= tabs.value.length) return
    const moved = tabs.value.splice(fromIndex, 1)[0]
    tabs.value.splice(toIndex, 0, moved)
    persist()
  }

  function updateTabTitle(id: string, title: string) {
    const tab = tabs.value.find(t => t.id === id)
    if (tab) {
      tab.title = title
      persist()
    }
  }

  /** Update a tab's path and title — used when navigating within an unpinned tab */
  function updateTabPath(id: string, path: string, title: string) {
    const tab = tabs.value.find(t => t.id === id)
    if (tab) {
      tab.path = path
      tab.title = title
      persist()
    }
  }

  /** Get a value from the active tab's per-tab state */
  function getTabState<T>(key: string, defaultValue: T): T {
    const tab = activeTab.value
    if (tab && key in tab.state) return tab.state[key] as T
    return defaultValue
  }

  /** Set a value on the active tab's per-tab state */
  function setTabState(key: string, value: unknown) {
    const tab = activeTab.value
    if (tab) {
      tab.state[key] = value
      persist()
    }
  }

  return {
    tabs,
    activeTabId,
    activeTab,
    closableTabs,
    tabCount,
    addTab,
    closeTab,
    closeOtherTabs,
    closeAllTabs,
    setActiveTab,
    pinTab,
    unpinTab,
    moveTab,
    updateTabTitle,
    getTabState,
    setTabState,
    updateTabPath,
    markTabUpdated,
    clearTabUpdate,
  }
})
